import React, { useState, useEffect } from 'react';
import {
  Box,
  Center,
  Table,
  Badge,
  Text,
  Card,
  Title,
  Group,
  TextInput,
  Container,
  Modal,
  NumberInput,
  Stack,
  LoadingOverlay,
  MultiSelect,
  Alert,
  Tooltip,
  Collapse,
  ActionIcon,
  useMantineTheme,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PageHeader } from '@/components/PageHeader';
import { CSSProperties } from 'react';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { IUser } from '@/types/User';
import {
  getListingsByAppId,
  updateUserPermissions,
  createApp,
  deleteApp as deleteAppApi,
  createLicense,
  updateLicense,
  deleteLicense as deleteLicenseApi,
  addAdmin,
  updateAdminApps,
  removeAdmin,
  UnifiedAppAPI,
} from '@/Api';
import { useMasterAdminData } from '@/hooks/useMasterAdminData';
import { IUserPermissions } from '@/types/UserPermission';
import {
  IApp,
  IAppLicense,
  IAppSubscription,
  FeatureFlag,
  DEFAULT_FEATURE_FLAGS,
} from '@/types/App.model';
import { IPopulatedListing } from '@/types/Listing.model';
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconShield,
  IconLicense,
  IconUsers,
  IconSettings,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconChevronDown,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { LicenseModal } from '@/components/MasterAdmin/LicenseModal';
import { MasterAdminStats } from '@/components/MasterAdmin/MasterAdminStats';
import { EditorChipButton } from '@/components/EditorChipButton';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { useStore } from '@/store/Store';

interface FullUser extends IUser {
  permissions?: IUserPermissions;
}

interface FullApp extends IApp {
  licensesToSubscriptions: Map<string, { license: IAppLicense; subscriptions: IAppSubscription[] }>;
  listings?: IPopulatedListing[];
}

interface SubscriptionInfo {
  app: IApp;
  license: IAppLicense;
  subscription: IAppSubscription;
}

interface License {
  id: string;
  name: string;
  userSlots: number;
  productSlots: number;
}

interface Admin {
  id: string;
  email: string;
  role: string;
}

/** Page section whose body folds away; the whole header toggles it, the chevron is the keyboard control. */
function CollapsibleSection({
  title,
  subtitle,
  icon,
  accentColor,
  defaultOpen = false,
  sectionStyle,
  headerStyle,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  accentColor: string;
  defaultOpen?: boolean;
  sectionStyle: CSSProperties;
  headerStyle: CSSProperties;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box style={sectionStyle}>
      <div
        style={{
          ...headerStyle,
          cursor: 'pointer',
          ...(open ? null : { borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }),
        }}
        onClick={() => setOpen((current) => !current)}
      >
        <PageHeader
          icon={icon}
          variant="section"
          title={title}
          subtitle={subtitle}
          accentColor={accentColor}
          mb={0}
          actions={
            // No own onClick: the click bubbles to the header, so mouse and keyboard toggle once.
            <ActionIcon variant="subtle" color="gray" aria-expanded={open} aria-label={title}>
              <IconChevronDown
                size={18}
                style={{
                  transform: open ? 'none' : 'rotate(-90deg)',
                  transition: 'transform 150ms ease',
                }}
              />
            </ActionIcon>
          }
        />
      </div>
      <Collapse in={open}>{children}</Collapse>
    </Box>
  );
}

// UserTable subcomponent
function UserTable({
  fullUsers,
  subscriptionInfo,
  searchTerm,
  onUpdateUser,
  styles,
  theme,
  isDark,
}: {
  fullUsers: Map<string, FullUser>;
  subscriptionInfo: Map<string, SubscriptionInfo>;
  searchTerm: string;
  onUpdateUser: (userId: string, updatedUser: FullUser) => void;
  styles: any;
  theme: any;
  isDark: boolean;
}) {
  const { t } = useTranslation();
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const handleEditStart = (
    userId: string,
    field: 'productSlots' | 'layoutSlots',
    currentValue: number
  ) => {
    setEditingCell(`${userId}-${field}`);
    setEditValue(currentValue.toString());
  };

  const handleEditSave = async (userId: string, field: 'productSlots' | 'layoutSlots') => {
    const user = fullUsers.get(userId);
    if (!user || !user.permissions) return;

    const newValue = parseInt(editValue);
    if (isNaN(newValue) || newValue < 0) {
      setEditingCell(null);
      return;
    }

    try {
      const updatedPermissions: IUserPermissions = {
        ...user.permissions,
        [field]: newValue,
      };

      const savedPermissions = await updateUserPermissions(userId, updatedPermissions);

      const updatedUser = {
        ...user,
        permissions: savedPermissions,
      };

      onUpdateUser(userId, updatedUser);
      setEditingCell(null);
      notifications.show({
        title: t('masterAdmin.notifications.adminSuccess'),
        message: t('masterAdmin.notifications.updateSlotsSuccess'),
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to update user permissions:', error);
      // Keep the cell open so the value can be corrected and retried.
      notifications.show({
        title: t('masterAdmin.notifications.adminError'),
        message: t('masterAdmin.notifications.updateSlotsError'),
        color: 'red',
      });
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent,
    userId: string,
    field: 'productSlots' | 'layoutSlots'
  ) => {
    if (e.key === 'Enter') {
      handleEditSave(userId, field);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const handleBlur = (userId: string, field: 'productSlots' | 'layoutSlots') => {
    handleEditSave(userId, field);
  };

  const filteredUsers = Array.from(fullUsers.values()).filter(
    (user) =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (filteredUsers.length === 0 && searchTerm) {
    return (
      <div style={styles.emptyState}>
        <Text size="lg" fw={500} style={{ marginBottom: '8px' }}>
          No users found
        </Text>
        <Text size="sm">No users match your search criteria</Text>
      </div>
    );
  }

  if (fullUsers.size === 0) {
    return (
      <div style={styles.emptyState}>
        <Text size="lg" fw={500} style={{ marginBottom: '8px' }}>
          No users found
        </Text>
        <Text size="sm">Loading user data or no users exist in the system</Text>
      </div>
    );
  }

  return (
    <div style={styles.tableContainer}>
      <Table striped highlightOnHover style={styles.table}>
        <Table.Thead style={styles.tableHeader}>
          <Table.Tr>
            <Table.Th style={styles.tableHeaderCell}>{t('masterAdmin.users.tableHeaders.userEmail')}</Table.Th>
            <Table.Th style={styles.tableHeaderCell}>{t('masterAdmin.users.tableHeaders.company')}</Table.Th>
            <Table.Th style={styles.tableHeaderCellCenter}>{t('masterAdmin.users.tableHeaders.productSlots')}</Table.Th>
            <Table.Th style={styles.tableHeaderCellCenter}>{t('masterAdmin.users.tableHeaders.inUse')}</Table.Th>
            <Table.Th style={styles.tableHeaderCellCenter}>{t('masterAdmin.users.tableHeaders.layoutSlots')}</Table.Th>
            <Table.Th style={styles.tableHeaderCellCenter}>{t('masterAdmin.users.tableHeaders.inUse')}</Table.Th>
            <Table.Th style={styles.tableHeaderCell}>{t('masterAdmin.users.tableHeaders.appSubscriptions')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
        {filteredUsers.map((user) => (
          <Table.Tr
            key={user._id}
            style={{ borderBottom: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}` }}
          >
            <Table.Td style={styles.tableCell}>
              <Text size="sm" fw={500} style={styles.userEmail}>
                {user.email}
              </Text>
              <Text size="xs" color="dimmed">
                {user.username}
              </Text>
            </Table.Td>
            <Table.Td style={styles.tableCell}>
              <Text size="sm" style={styles.companyText}>
                {user.companyName || 'N/A'}
              </Text>
            </Table.Td>
            <Table.Td style={styles.tableCellCenter}>
              {editingCell === `${user._id}-productSlots` ? (
                <TextInput
                  value={editValue}
                  onChange={(e) => setEditValue(e.currentTarget.value)}
                  onKeyDown={(e) => handleKeyPress(e, user._id, 'productSlots')}
                  onBlur={(e) => handleBlur(user._id, 'productSlots')}
                  size="xs"
                  autoFocus
                />
              ) : (
                <Badge
                  color="blue"
                  variant="light"
                  size="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    handleEditStart(user._id, 'productSlots', user.permissions?.productSlots ?? 0)
                  }
                >
                  {user.permissions?.productSlots ?? 0}
                </Badge>
              )}
            </Table.Td>
            <Table.Td style={styles.tableCellCenter}>
              <Group justify="center" gap={4}>
                <Text size="sm" style={styles.companyText}>
                  {user.products?.length ?? 0}
                </Text>
                {user.permissions?.productSlots &&
                  user.products?.length > user.permissions.productSlots && (
                    <Badge color="red" variant="filled" size="xs">
                      Over limit
                    </Badge>
                  )}
              </Group>
            </Table.Td>
            <Table.Td style={styles.tableCellCenter}>
              {editingCell === `${user._id}-layoutSlots` ? (
                <TextInput
                  value={editValue}
                  onChange={(e) => setEditValue(e.currentTarget.value)}
                  onKeyDown={(e) => handleKeyPress(e, user._id, 'layoutSlots')}
                  onBlur={(e) => handleBlur(user._id, 'layoutSlots')}
                  size="xs"
                  autoFocus
                />
              ) : (
                <Badge
                  color="blue"
                  variant="light"
                  size="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    handleEditStart(user._id, 'layoutSlots', user.permissions?.layoutSlots ?? 0)
                  }
                >
                  {user.permissions?.layoutSlots ?? 0}
                </Badge>
              )}
            </Table.Td>
            <Table.Td style={styles.tableCellCenter}>
              <Group justify="center" gap={4}>
                <Text size="sm" style={styles.companyText}>
                  {user.layouts?.length ?? 0}
                </Text>
                {user.permissions?.layoutSlots &&
                  user.layouts?.length > user.permissions.layoutSlots && (
                    <Badge color="red" variant="filled" size="xs">
                      Over limit
                    </Badge>
                  )}
              </Group>
            </Table.Td>
            <Table.Td style={styles.tableCell}>
              {user.appSubscription_ids && user.appSubscription_ids.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {user.appSubscription_ids.map((subscriptionId) => {
                    const subInfo = subscriptionInfo.get(subscriptionId);
                    if (subInfo) {
                      return (
                        <Text key={subscriptionId} size="xs" style={{ color: '#6b7280' }}>
                          {subInfo.app.appName}: {subInfo.license.licenseName}
                        </Text>
                      );
                    }
                    return (
                      <Text key={subscriptionId} size="xs" style={{ color: '#ef4444' }}>
                        Unknown subscription
                      </Text>
                    );
                  })}
                </div>
              ) : (
                <Badge color="gray" variant="light" size="sm">
                  No subscriptions
                </Badge>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
    </div>
  );
}

// AppsTable subcomponent
function AppsTable({
  fullApps,
  allUsers,
  searchTerm,
  styles,
}: {
  fullApps: Map<string, FullApp>;
  allUsers: Map<string, FullUser>;
  searchTerm: string;
  styles: any;
}) {
  const filteredApps = Array.from(fullApps.values()).filter(
    (app) =>
      app.appName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.summaryDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.adminUser_ids?.some((adminId) => {
        const adminUser = allUsers.get(adminId);
        return adminUser?.email?.toLowerCase().includes(searchTerm.toLowerCase());
      })
  );

  if (filteredApps.length === 0 && searchTerm) {
    return (
      <div style={styles.emptyState}>
        <Text size="lg" fw={500} style={{ marginBottom: '8px' }}>
          No apps found
        </Text>
        <Text size="sm">No apps match your search criteria</Text>
      </div>
    );
  }

  if (fullApps.size === 0) {
    return (
      <div style={styles.emptyState}>
        <Text size="lg" fw={500} style={{ marginBottom: '8px' }}>
          No apps found
        </Text>
        <Text size="sm">Loading app data or no apps exist in the system</Text>
      </div>
    );
  }

  return (
    <div style={styles.tableContainer}>
      <Table striped highlightOnHover style={styles.table}>
        <Table.Thead style={styles.tableHeader}>
          <Table.Tr>
            <Table.Th style={styles.tableHeaderCell}>App Name</Table.Th>
            <Table.Th style={styles.tableHeaderCellCenter}>User Slots</Table.Th>
            <Table.Th style={styles.tableHeaderCell}>Licenses</Table.Th>
            <Table.Th style={styles.tableHeaderCellCenter}>Product Slots</Table.Th>
            <Table.Th style={styles.tableHeaderCellCenter}>Listed</Table.Th>
            <Table.Th style={styles.tableHeaderCellCenter}>Unlisted</Table.Th>
            <Table.Th style={styles.tableHeaderCellCenter}>Pending</Table.Th>
            <Table.Th style={styles.tableHeaderCell}>App Admins</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
        {filteredApps.map((app) => {
          const listedProducts =
            app.listings?.filter((listing) => listing.status === 'LISTED').length || 0;
          const unlistedProducts =
            app.listings?.filter((listing) => listing.status === 'UNLISTED').length || 0;
          const pendingProducts =
            app.listings?.filter((listing) => listing.status === 'PENDING').length || 0;

          return (
            <Table.Tr key={app._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
              <Table.Td style={styles.tableCell}>
                <Text size="sm" fw={500} style={styles.userEmail}>
                  {app.appName}
                </Text>
                <Text size="xs" color="dimmed">
                  {app.summaryDescription || 'No description'}
                </Text>
              </Table.Td>
              <Table.Td style={styles.tableCellCenter}>
                <Badge color="blue" variant="light" size="sm">
                  {app.userLimit}
                </Badge>
              </Table.Td>
              <Table.Td style={styles.tableCell}>
                {app.licensesToSubscriptions.size > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {Array.from(app.licensesToSubscriptions.values()).map(
                      ({ license, subscriptions }) => (
                        <div
                          key={license._id}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          <Text size="xs" style={{ color: '#374151' }}>
                            {license.licenseName}
                          </Text>
                          <Badge color="gray" variant="light" size="xs">
                            {subscriptions.length} users
                          </Badge>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <Badge color="gray" variant="light" size="sm">
                    No licenses
                  </Badge>
                )}
              </Table.Td>
              <Table.Td style={styles.tableCellCenter}>
                <Badge color="blue" variant="light" size="sm">
                  {app.modelLimit}
                </Badge>
              </Table.Td>
              <Table.Td style={styles.tableCellCenter}>
                <Badge color="gray" variant="light" size="sm">
                  {listedProducts}
                </Badge>
              </Table.Td>
              <Table.Td style={styles.tableCellCenter}>
                <Badge color="gray" variant="light" size="sm">
                  {unlistedProducts}
                </Badge>
              </Table.Td>
              <Table.Td style={styles.tableCellCenter}>
                <Badge color="gray" variant="light" size="sm">
                  {pendingProducts}
                </Badge>
              </Table.Td>
              <Table.Td style={styles.tableCell}>
                {app.adminUser_ids && app.adminUser_ids.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      backgroundColor: '#dbeafe',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: 500,
                      border: 'none',
                    }}
                  >
                    {app.adminUser_ids.map((adminId) => {
                      const adminUser = allUsers.get(adminId);
                      return (
                        <Text key={adminId} size="xs" style={{ color: '#1864ab', fontWeight: 500 }}>
                          {adminUser?.email || `Unknown (${adminId})`}
                        </Text>
                      );
                    })}
                  </div>
                ) : (
                  <Badge color="blue" variant="light" size="sm">
                    No admins
                  </Badge>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
        </Table.Tbody>
      </Table>
    </div>
  );
}

/**
 * MasterAdmin: Main dashboard for ROBOTOP employees
 * Provides full platform management capabilities
 * - App management (create, delete, modify limits)
 * - License management
 * - Admin management (add/remove admins)
 * - User management and permissions
 * - Subscription management
 */
export function MasterAdmin() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const accentColor = useStore((state) => state.userProfile.qrCodeColor) || DEFAULT_QR_CODE_COLOR;

  // Dynamic styles based on theme
  const styles = {
    section: {
      width: '100%',
      maxWidth: '1200px',
      backgroundColor: isDark ? theme.colors.dark[6] : 'white',
      borderRadius: '12px',
      padding: '24px',
      margin: '16px',
      boxShadow: isDark
        ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
        : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
    } as CSSProperties,

    cardHeader: {
      borderBottom: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
      paddingBottom: '16px',
      marginBottom: '24px',
    } as CSSProperties,

    table: {
      borderRadius: '8px',
      overflow: 'hidden',
      border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
      minWidth: '800px', // Minimum width for table content
    } as CSSProperties,

    tableContainer: {
      width: '100%',
      overflowX: 'auto', // Enable horizontal scrolling
      overflowY: 'hidden',
      WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
      borderRadius: '8px',
      border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
    } as CSSProperties,

    tableHeader: {
      backgroundColor: isDark ? theme.colors.dark[5] : '#f9fafb',
    } as CSSProperties,

    tableCell: {
      padding: '16px 12px',
    } as CSSProperties,

    tableCellCenter: {
      padding: '16px 12px',
      textAlign: 'center',
    } as CSSProperties,

    tableHeaderCell: {
      padding: '16px 12px',
      fontWeight: 600,
      color: isDark ? theme.colors.gray[2] : '#374151',
    } as CSSProperties,

    // Shrink-to-content columns (width 1% = as narrow as the content allows), so spare width
    // goes to the text-heavy columns. Header may still wrap; cells stay on one line.
    fitHeaderCell: {
      padding: '16px 12px',
      fontWeight: 600,
      color: isDark ? theme.colors.gray[2] : '#374151',
      width: '1%',
    } as CSSProperties,

    fitCell: {
      padding: '16px 12px',
      width: '1%',
      whiteSpace: 'nowrap',
    } as CSSProperties,

    tableHeaderCellCenter: {
      padding: '16px 12px',
      fontWeight: 600,
      color: isDark ? theme.colors.gray[2] : '#374151',
      textAlign: 'center',
    } as CSSProperties,

    emptyState: {
      textAlign: 'center',
      padding: '48px 24px',
      color: isDark ? theme.colors.gray[4] : '#6b7280',
      backgroundColor: isDark ? theme.colors.dark[5] : '#f9fafb',
      borderRadius: '8px',
      border: `2px dashed ${isDark ? theme.colors.dark[3] : '#d1d5db'}`,
    } as CSSProperties,

    titleText: {
      margin: 0,
      color: isDark ? theme.colors.gray[2] : '#374151',
    } as CSSProperties,

    subtitleText: {
      marginTop: '4px',
    } as CSSProperties,

    userEmail: {
      color: isDark ? theme.colors.gray[1] : '#111827',
    } as CSSProperties,

    companyText: {
      color: isDark ? theme.colors.gray[3] : '#374151',
    } as CSSProperties,

    statsCard: {
      backgroundColor: isDark ? theme.colors.dark[6] : 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: isDark
        ? '0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.2)'
        : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      border: `1px solid ${isDark ? theme.colors.dark[4] : '#e5e7eb'}`,
    } as CSSProperties,
  };

  // Use custom hook for data management
  const {
    apps,
    licenses,
    admins,
    totalUsers,
    portfolioApps,
    portfolioLicenses,
    subscriptions,
    users,
    permissions,
    productTotals,
    isLoading,
    isAuthorized,
    error,
    refreshApps,
    refreshLicenses,
    refreshAdmins,
    refreshUsers,
  } = useMasterAdminData();

  // Local state for UI
  const [appName, setAppName] = useState('');
  const [openLicenseModal, setOpenLicenseModal] = useState(false);
  const [openAdminModal, setOpenAdminModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [editingLicense, setEditingLicense] = useState<IAppLicense | null>(null);
  const [licenseFormData, setLicenseFormData] = useState({
    appId: '',
    licenseName: '',
    featureFlags: { ...DEFAULT_FEATURE_FLAGS },
  });
  const [editingAppLimits, setEditingAppLimits] = useState<any>(null);
  const [limitsFormData, setLimitsFormData] = useState({
    userLimit: 0,
    modelLimit: 0,
    appName: '',
    summaryDescription: '',
    plattforms: [] as string[],
  });
  const [editingInlineLimits, setEditingInlineLimits] = useState<{
    appId: string;
    field: 'userLimit' | 'modelLimit';
  } | null>(null);
  const [inlineLimitValue, setInlineLimitValue] = useState<number>(0);
  const [errorModal, setErrorModal] = useState<{ opened: boolean; title: string; message: string }>(
    {
      opened: false,
      title: '',
      message: '',
    }
  );
  const [adminEmail, setAdminEmail] = useState('');
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const [editingAdmin, setEditingAdmin] = useState<any>(null);
  const [openEditAdminModal, setOpenEditAdminModal] = useState(false);

  // Derived state
  const [fullUsers, setFullUsers] = useState<Map<string, FullUser>>(new Map());
  const [listings, setListings] = useState<Map<string, IPopulatedListing[]>>(new Map());
  const [fullApps, setFullApps] = useState<Map<string, FullApp>>(new Map());
  const [subscriptionInfo, setSubscriptionInfo] = useState<Map<string, SubscriptionInfo>>(
    new Map()
  );

  // Search state
  const [appsSearchTerm, setAppsSearchTerm] = useState('');
  const [usersSearchTerm, setUsersSearchTerm] = useState('');

  // Fetch listings for each app when portfolioApps changes
  useEffect(() => {
    if (portfolioApps.length === 0) return;

    const fetchListings = async () => {
      const listingsMap = new Map<string, IPopulatedListing[]>();

      for (const app of portfolioApps) {
        try {
          const appListings = await getListingsByAppId(app._id);
          listingsMap.set(app._id, appListings);
        } catch (error) {
          console.error(`Failed to fetch listings for app ${app._id}:`, error);
          listingsMap.set(app._id, []);
        }
      }

      setListings(listingsMap);
    };

    fetchListings();
  }, [portfolioApps]);

  useEffect(() => {
    if (users.length === 0 || permissions.length === 0) return;
    const fullUsersRecord = users.reduce((acc, user) => {
      // Match permissions by user ID (permissions._id should match user._id)
      const userPermissions = permissions.find((p) => p._id === user._id);
      acc.set(user._id, {
        ...user,
        permissions: userPermissions, // This can be undefined if not found
      });
      return acc;
    }, new Map<string, FullUser>());

    setFullUsers(fullUsersRecord);
  }, [users, permissions]);

  useEffect(() => {
    if (portfolioApps.length === 0 || portfolioLicenses.length === 0 || subscriptions.length === 0)
      return;
    const fullAppsRecord = new Map<string, FullApp>();
    const fullSubscriptionInfo = new Map<string, SubscriptionInfo>();

    portfolioApps.forEach((app) => {
      const fullApp: FullApp = {
        ...app,
        listings: listings.get(app._id) || [],
        licensesToSubscriptions: new Map<
          string,
          { license: IAppLicense; subscriptions: IAppSubscription[] }
        >(),
      };
      const licensesForApp = portfolioLicenses.filter((l) => l.app_id === app._id);
      licensesForApp.forEach((license) => {
        const subscriptionsForLicense = subscriptions.filter(
          (s) => s.appLicense_id === license._id
        );
        fullApp.licensesToSubscriptions.set(license._id, {
          license,
          subscriptions: subscriptionsForLicense,
        });
        subscriptionsForLicense.forEach((subscription) => {
          fullSubscriptionInfo.set(subscription._id, { app, license, subscription });
        });
      });
      fullAppsRecord.set(app._id, fullApp);
    });
    setFullApps(fullAppsRecord);
    setSubscriptionInfo(fullSubscriptionInfo);
  }, [portfolioApps, portfolioLicenses, subscriptions, listings]);

  // MasterAdmin handler functions
  const handleCreateApp = async () => {
    if (!appName.trim()) return;
    try {
      await createApp(appName.trim());
      setAppName('');

      notifications.show({
        title: t('masterAdmin.notifications.adminSuccess'),
        message: t('masterAdmin.notifications.createAppSuccess'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      // Refresh data using hook
      await refreshApps();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || t('masterAdmin.notifications.createAppError');
      const errorTitle =
        err.response?.status === 403
          ? t('masterAdmin.notifications.adminError')
          : err.response?.status === 404
            ? t('masterAdmin.permissions.appNotFound')
            : t('masterAdmin.notifications.adminError');

      notifications.show({
        title: errorTitle,
        message: errorMessage,
        color: 'red',
        icon: <IconX size={16} />,
      });

      setErrorModal({
        opened: true,
        title: errorTitle,
        message: errorMessage,
      });
    }
  };

  const handleAddLicense = () => {
    setLicenseFormData({ appId: '', licenseName: '', featureFlags: { ...DEFAULT_FEATURE_FLAGS } });
    setEditingLicense(null);
    setOpenLicenseModal(true);
  };

  const handleAddAdmin = () => {
    setAdminEmail('');
    setSelectedAppIds([]);
    setOpenAdminModal(true);
  };

  const handleDeleteApp = async (appId: string) => {
    if (!window.confirm(t('masterAdmin.apps.deleteConfirm'))) return;
    try {
      await deleteAppApi(appId);

      notifications.show({
        title: t('masterAdmin.notifications.adminSuccess'),
        message: t('masterAdmin.notifications.deleteAppSuccess'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      // Refresh data using hook
      await refreshApps();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || t('masterAdmin.notifications.deleteAppError');
      const errorTitle =
        err.response?.status === 403
          ? t('masterAdmin.notifications.adminError')
          : err.response?.status === 404
            ? t('masterAdmin.permissions.appNotFound')
            : t('masterAdmin.notifications.adminError');

      notifications.show({
        title: errorTitle,
        message: errorMessage,
        color: 'red',
        icon: <IconX size={16} />,
      });

      setErrorModal({
        opened: true,
        title: errorTitle,
        message: errorMessage,
      });
    }
  };

  const handleEditLicense = (license: IAppLicense) => {
    setEditingLicense(license);
    setLicenseFormData({
      appId: license.app_id,
      licenseName: license.licenseName,
      featureFlags: { ...DEFAULT_FEATURE_FLAGS, ...license.featureFlags },
    });
    setOpenLicenseModal(true);
  };

  const handleSaveLicense = async () => {
    if (!licenseFormData.appId || !licenseFormData.licenseName.trim()) return;
    try {
      if (editingLicense) {
        await updateLicense(editingLicense._id, {
          licenseName: licenseFormData.licenseName.trim(),
          featureFlags: licenseFormData.featureFlags,
        });
        notifications.show({
          title: t('masterAdmin.notifications.adminSuccess'),
          message: t('masterAdmin.notifications.updateLicenseSuccess'),
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else {
        await createLicense(
          licenseFormData.appId,
          licenseFormData.licenseName.trim(),
          { featureFlags: licenseFormData.featureFlags }
        );
        notifications.show({
          title: t('masterAdmin.notifications.adminSuccess'),
          message: t('masterAdmin.notifications.createLicenseSuccess'),
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      }
      setOpenLicenseModal(false);

      // Refresh licenses data using hook
      await refreshLicenses();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || t('masterAdmin.notifications.createLicenseError');
      const errorTitle =
        err.response?.status === 403
          ? t('masterAdmin.notifications.adminError')
          : err.response?.status === 404
            ? t('masterAdmin.permissions.appNotFound')
            : t('masterAdmin.notifications.adminError');

      notifications.show({
        title: errorTitle,
        message: errorMessage,
        color: 'red',
        icon: <IconX size={16} />,
      });

      setErrorModal({
        opened: true,
        title: errorTitle,
        message: errorMessage,
      });
    }
  };

  const handleDeleteLicense = async (licenseId: string) => {
    if (!window.confirm(t('masterAdmin.licenses.deleteConfirm'))) return;
    try {
      await deleteLicenseApi(licenseId);

      notifications.show({
        title: t('masterAdmin.notifications.adminSuccess'),
        message: t('masterAdmin.notifications.deleteLicenseSuccess'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      // Refresh licenses data using hook
      await refreshLicenses();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || t('masterAdmin.notifications.deleteLicenseError');
      const errorTitle =
        err.response?.status === 403
          ? t('masterAdmin.notifications.adminError')
          : err.response?.status === 404
            ? t('masterAdmin.permissions.licenseNotFound')
            : t('masterAdmin.notifications.adminError');

      notifications.show({
        title: errorTitle,
        message: errorMessage,
        color: 'red',
        icon: <IconX size={16} />,
      });

      setErrorModal({
        opened: true,
        title: errorTitle,
        message: errorMessage,
      });
    }
  };

  const handleEditAppLimits = (app: any) => {
    setEditingAppLimits(app);
    setLimitsFormData({
      userLimit: app.userLimit || 0,
      modelLimit: app.modelLimit || 0,
      appName: app.appName || '',
      summaryDescription: app.summaryDescription || '',
      plattforms: app.plattforms || [],
    });
  };

  const handleSaveAppLimits = async () => {
    if (!editingAppLimits) return;
    try {
      // Create updates object with only changed fields
      const updates: Partial<IApp> = {};
      const changedFields: string[] = [];

      if (limitsFormData.userLimit !== editingAppLimits.userLimit) {
        updates.userLimit = limitsFormData.userLimit;
        changedFields.push(t('masterAdmin.tables.userLimit'));
      }
      if (limitsFormData.modelLimit !== editingAppLimits.modelLimit) {
        updates.modelLimit = limitsFormData.modelLimit;
        changedFields.push(t('masterAdmin.tables.modelLimit'));
      }
      if (limitsFormData.appName !== editingAppLimits.appName) {
        updates.appName = limitsFormData.appName;
        changedFields.push(t('masterAdmin.tables.appName'));
      }
      if (limitsFormData.summaryDescription !== editingAppLimits.summaryDescription) {
        updates.summaryDescription = limitsFormData.summaryDescription;
        changedFields.push(t('masterAdmin.apps.description'));
      }
      if (
        JSON.stringify(limitsFormData.plattforms) !== JSON.stringify(editingAppLimits.plattforms)
      ) {
        updates.plattforms = limitsFormData.plattforms;
        changedFields.push(t('masterAdmin.apps.platforms'));
      }

      // Only update if there are changes
      if (Object.keys(updates).length > 0) {
        await UnifiedAppAPI.updateFull(editingAppLimits._id, updates);
        notifications.show({
          title: t('masterAdmin.notifications.adminSuccess'),
          message: t('masterAdmin.notifications.updateSettingsSuccess'),
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      }

      setEditingAppLimits(null);

      // Refresh apps data using hook
      await refreshApps();
    } catch (err: any) {
      // Get error information from backend response
      const errorResponse = err.response?.data;
      let errorField = 'Settings';
      let errorMessage = errorResponse?.message || err.message;

      // Use field information from backend if available
      if (errorResponse?.field) {
        const fieldMap: { [key: string]: string } = {
          appName: t('masterAdmin.tables.appName'),
          userLimit: t('masterAdmin.tables.userLimit'),
          modelLimit: t('masterAdmin.tables.modelLimit'),
          summaryDescription: t('masterAdmin.apps.description'),
          plattforms: t('masterAdmin.apps.platforms'),
        };
        errorField = fieldMap[errorResponse.field] || t('masterAdmin.apps.settings');
      } else {
        // Fallback: Check if error message contains specific field information
        if (errorMessage) {
          const lowerMessage = errorMessage.toLowerCase();
          if (lowerMessage.includes('appname') || lowerMessage.includes('app name')) {
            errorField = t('masterAdmin.tables.appName');
          } else if (lowerMessage.includes('userlimit') || lowerMessage.includes('user limit')) {
            errorField = t('masterAdmin.tables.userLimit');
          } else if (lowerMessage.includes('modellimit') || lowerMessage.includes('model limit')) {
            errorField = t('masterAdmin.tables.modelLimit');
          } else if (lowerMessage.includes('description') || lowerMessage.includes('summary')) {
            errorField = t('masterAdmin.apps.description');
          } else if (lowerMessage.includes('platform') || lowerMessage.includes('plattform')) {
            errorField = t('masterAdmin.apps.platforms');
          }
        }
      }

      const finalErrorMessage =
        errorMessage || t('masterAdmin.notifications.updateSettingsError', { field: errorField });
      const errorTitle =
        err.response?.status === 403
          ? t('masterAdmin.notifications.adminError')
          : err.response?.status === 404
            ? t('masterAdmin.permissions.appNotFound')
            : t('masterAdmin.notifications.updateSettingsErrorTitle', { field: errorField });

      notifications.show({
        title: errorTitle,
        message: finalErrorMessage,
        color: 'red',
        icon: <IconX size={16} />,
      });

      setErrorModal({
        opened: true,
        title: errorTitle,
        message: finalErrorMessage,
      });
    }
  };

  const handleInlineEditStart = (
    appId: string,
    field: 'userLimit' | 'modelLimit',
    currentValue: number
  ) => {
    setEditingInlineLimits({ appId, field });
    setInlineLimitValue(currentValue);
  };

  const handleInlineEditSave = async (appId: string, field: 'userLimit' | 'modelLimit') => {
    try {
      const app = apps.find((a) => a._id === appId);
      if (!app) return;

      const userLimit = field === 'userLimit' ? inlineLimitValue : app.userLimit || 0;
      const modelLimit = field === 'modelLimit' ? inlineLimitValue : app.modelLimit || 0;

      await UnifiedAppAPI.updateLimits(appId, { userLimit, modelLimit });
      setEditingInlineLimits(null);

      notifications.show({
        title: t('masterAdmin.notifications.adminSuccess'),
        message: t('masterAdmin.notifications.updateLimitsSuccess'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      // Refresh apps data using hook
      await refreshApps();
    } catch (err: any) {
      const errorResponse = err.response?.data;
      const fieldName =
        field === 'userLimit' ? t('masterAdmin.tables.userLimit') : t('masterAdmin.tables.modelLimit');
      const errorMessage =
        errorResponse?.message || err.message || t('masterAdmin.notifications.updateLimitsError');
      const errorTitle =
        err.response?.status === 403
          ? t('masterAdmin.notifications.adminError')
          : err.response?.status === 404
            ? t('masterAdmin.permissions.appNotFound')
            : t('masterAdmin.notifications.updateSettingsErrorTitle', { field: fieldName });

      notifications.show({
        title: errorTitle,
        message: errorMessage,
        color: 'red',
        icon: <IconX size={16} />,
      });

      setErrorModal({
        opened: true,
        title: errorTitle,
        message: errorMessage,
      });
    }
  };

  const handleInlineEditCancel = () => {
    setEditingInlineLimits(null);
  };

  const handleInlineKeyPress = (
    e: React.KeyboardEvent,
    appId: string,
    field: 'userLimit' | 'modelLimit'
  ) => {
    if (e.key === 'Enter') {
      handleInlineEditSave(appId, field);
    } else if (e.key === 'Escape') {
      handleInlineEditCancel();
    }
  };

  const handleInlineBlur = (appId: string, field: 'userLimit' | 'modelLimit') => {
    handleInlineEditSave(appId, field);
  };

  const handleSaveAdmin = async () => {
    if (!adminEmail.trim() || selectedAppIds.length === 0) return;
    try {
      if (editingAdmin) {
        // Update existing admin
        await updateAdminApps(editingAdmin._id, selectedAppIds);
        notifications.show({
          title: t('masterAdmin.notifications.adminSuccess'),
          message: t('masterAdmin.notifications.updateAdminSuccess'),
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else {
        // Add new admin
        await addAdmin(adminEmail.trim(), selectedAppIds.join(','));
        notifications.show({
          title: t('masterAdmin.notifications.adminSuccess'),
          message: t('masterAdmin.notifications.addAdminToAppSuccess'),
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      }

      // Refresh data using hooks
      await Promise.all([refreshApps(), refreshAdmins()]);
      setOpenAdminModal(false);
      setEditingAdmin(null);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        (editingAdmin ? t('masterAdmin.notifications.updateAdminError') : t('masterAdmin.notifications.addAdminToAppError'));
      const errorTitle =
        err.response?.status === 403
          ? t('masterAdmin.notifications.adminError')
          : err.response?.status === 404
            ? t('masterAdmin.permissions.userNotFound')
            : t('masterAdmin.notifications.adminError');

      notifications.show({
        title: errorTitle,
        message: errorMessage,
        color: 'red',
        icon: <IconX size={16} />,
      });

      setErrorModal({
        opened: true,
        title: errorTitle,
        message: errorMessage,
      });
    }
  };

  const handleDeleteAdmin = async (email: string) => {
    if (!window.confirm(t('masterAdmin.admins.removeConfirm'))) return;
    try {
      await removeAdmin(email);

      notifications.show({
        title: t('masterAdmin.notifications.adminSuccess'),
        message: t('masterAdmin.notifications.removeAdminSuccess'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      // Refresh admins data using hook
      await refreshAdmins();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || t('masterAdmin.notifications.removeAdminError');
      const errorTitle =
        err.response?.status === 403
          ? t('masterAdmin.notifications.adminError')
          : err.response?.status === 404
            ? t('masterAdmin.permissions.userNotFound')
            : t('masterAdmin.notifications.adminError');

      notifications.show({
        title: errorTitle,
        message: errorMessage,
        color: 'red',
        icon: <IconX size={16} />,
      });

      setErrorModal({
        opened: true,
        title: errorTitle,
        message: errorMessage,
      });
    }
  };

  const handleEditAdmin = (admin: any) => {
    setEditingAdmin(admin);
    setAdminEmail(admin.email);
    setSelectedAppIds(admin.apps.map((app: any) => app._id));
    setOpenAdminModal(true);
  };

  // Show loading state
  if (isLoading) {
    return (
      <Center style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <LoadingOverlay visible={true} />
      </Center>
    );
  }

  // Show unauthorized access page
  if (isAuthorized === false) {
    return (
      <Center
        style={{
          minHeight: '100vh',
          backgroundColor: isDark ? theme.colors.dark[7] : '#f8fafc',
          position: 'relative',
        }}
      >
        {/* Blurred background content */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            filter: 'blur(8px)',
            opacity: 0.3,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Container size="xl" style={{ width: '100%', padding: '20px' }}>
            <Box style={styles.section}>
              <div style={styles.cardHeader}>
                <PageHeader
                  icon={<IconShield size={22} stroke={1.75} />}
                  title={t('masterAdmin.header.title')}
                  subtitle={t('masterAdmin.header.description')}
                  accentColor="#3b82f6"
                  mb={0}
                />
              </div>
            </Box>
          </Container>
        </div>

        {/* Unauthorized access overlay */}
        <div
          style={{
            position: 'relative',
            zIndex: 10,
            textAlign: 'center',
            padding: '40px',
            maxWidth: '500px',
          }}
        >
          <Card
            shadow="xl"
            radius="lg"
            p="xl"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Stack align="center" gap="lg">
              {/* Icon */}
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}
              >
                <IconShield size={40} color="#dc2626" />
              </div>

              {/* Title */}
              <Title order={2} size="h3" c="dark.7" ta="center">
                {t('masterAdmin.permissions.pageAccessDenied')}
              </Title>

              {/* Description */}
              <Text size="lg" c="dimmed" ta="center" maw={400}>
                {t('masterAdmin.permissions.pageNotAuthorized')}
              </Text>

              {/* Additional info */}
              <Alert
                icon={<IconAlertCircle size={20} />}
                color="red"
                variant="light"
                style={{ width: '100%' }}
              >
                <Text size="sm" ta="center">
                  {t('masterAdmin.permissions.contactAdmin')}
                </Text>
              </Alert>

              {/* Action button */}
              <EditorChipButton
                variant="primary"
                accentColor={accentColor}
                leftIcon={<IconShield size={15} stroke={1.75} />}
                onClick={() => window.history.back()}
                style={{ marginTop: 12 }}
              >
                {t('masterAdmin.permissions.goBack')}
              </EditorChipButton>
            </Stack>
          </Card>
        </div>
      </Center>
    );
  }

  return (
    <SessionAuth>
      <Center
        style={{
          minHeight: '100vh',
          backgroundColor: isDark ? theme.colors.dark[7] : '#f8fafc',
          padding: '20px',
        }}
      >
        <Container size="xl" style={{ width: '100%' }}>
          <LoadingOverlay visible={false} />

          {/* MasterAdmin Header Section */}
          <Box style={styles.section}>
            <div style={styles.cardHeader}>
              <PageHeader
                icon={<IconShield size={22} stroke={1.75} />}
                title={t('masterAdmin.header.title')}
                subtitle={t('masterAdmin.header.description')}
                accentColor="#3b82f6"
                mb={0}
              />
            </div>

            {/* Stats Cards */}
            <MasterAdminStats
              appsCount={apps.length}
              licensesCount={licenses.length}
              totalUsers={totalUsers}
              adminsCount={admins.length}
              // Same rule as licenseUtils: active while the expiration date is in the future.
              activeSubscriptionsCount={
                subscriptions.filter((subscription) => new Date(subscription.expirationDate) > new Date())
                  .length
              }
              productTotals={productTotals}
            />
          </Box>

          {/* MasterAdmin Apps Section */}
          <CollapsibleSection
            icon={<IconShield size={18} stroke={1.75} />}
            title={t('masterAdmin.apps.title')}
            subtitle={t('masterAdmin.apps.manage')}
            accentColor="#3b82f6"
            defaultOpen
            sectionStyle={styles.section}
            headerStyle={styles.cardHeader}
          >

            {/* App Creation Section */}
            <Card withBorder p="lg" mb="xl">
              <PageHeader
                icon={<IconPlus size={18} stroke={1.75} />}
                variant="section"
                title={t('masterAdmin.apps.create')}
                accentColor="#3b82f6"
                titleAs="h3"
              >
                <Group align="flex-end">
                  <TextInput
                    placeholder={t('masterAdmin.apps.namePlaceholder')}
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    style={{ flex: 1 }}
                    size="md"
                    leftSection={<IconShield size={16} />}
                  />
                  <EditorChipButton
                    variant="primary"
                    accentColor={accentColor}
                    leftIcon={<IconPlus size={15} stroke={1.75} />}
                    onClick={handleCreateApp}
                  >
                    {t('masterAdmin.apps.create')}
                  </EditorChipButton>
                </Group>
              </PageHeader>
            </Card>

            {/* Search Section */}
            <TextInput
              placeholder="Search apps..."
              value={appsSearchTerm}
              onChange={(e) => setAppsSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              size="sm"
              style={{ width: '100%', marginBottom: '16px' }}
            />

            {fullApps.size === 0 ? (
              <div style={styles.emptyState}>
                <IconShield size={48} color="#d1d5db" />
                <Text size="lg" mt="md" fw={500}>
                  {t('masterAdmin.emptyStates.noAppsFound')}
                </Text>
                <Text size="sm" c="dimmed" mt="xs">
                  {t('masterAdmin.apps.createFirst')}
                </Text>
              </div>
            ) : Array.from(fullApps.values()).filter(
                (app) =>
                  app.appName.toLowerCase().includes(appsSearchTerm.toLowerCase()) ||
                  app.summaryDescription?.toLowerCase().includes(appsSearchTerm.toLowerCase()) ||
                  app.adminUser_ids?.some((adminId) => {
                    const adminUser = fullUsers.get(adminId);
                    return adminUser?.email?.toLowerCase().includes(appsSearchTerm.toLowerCase());
                  })
              ).length === 0 && appsSearchTerm ? (
              <div style={styles.emptyState}>
                <IconSearch size={48} color="#d1d5db" />
                <Text size="lg" mt="md" fw={500}>
                  No apps found
                </Text>
                <Text size="sm" c="dimmed" mt="xs">
                  No apps match your search criteria
                </Text>
              </div>
            ) : (
              <div style={styles.tableContainer}>
                <Table style={styles.table}>
                  <Table.Thead style={styles.tableHeader}>
                    <Table.Tr>
                      <Table.Th style={styles.tableHeaderCell}>{t('masterAdmin.tables.appName')}</Table.Th>
                      <Table.Th style={styles.tableHeaderCell}>Licenses</Table.Th>
                      <Table.Th style={styles.fitHeaderCell}>Platforms</Table.Th>
                      <Table.Th style={styles.fitHeaderCell}>{t('masterAdmin.tables.userLimit')}</Table.Th>
                      <Table.Th style={styles.fitHeaderCell}>
                        {t('masterAdmin.tables.modelLimit')}
                      </Table.Th>
                      <Table.Th style={styles.fitHeaderCell}>
                        Listed
                        <Text size="xs" c="dimmed" fw={400} style={{ whiteSpace: 'nowrap' }}>
                          / unlisted / pending
                        </Text>
                      </Table.Th>
                      <Table.Th style={styles.tableHeaderCell}>{t('masterAdmin.tables.admins')}</Table.Th>
                      <Table.Th style={styles.fitHeaderCell}>{t('masterAdmin.tables.actions')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                  {Array.from(fullApps.values())
                    .filter(
                      (app) =>
                        app.appName.toLowerCase().includes(appsSearchTerm.toLowerCase()) ||
                        app.summaryDescription
                          ?.toLowerCase()
                          .includes(appsSearchTerm.toLowerCase()) ||
                        app.adminUser_ids?.some((adminId) => {
                          const adminUser = fullUsers.get(adminId);
                          return adminUser?.email
                            ?.toLowerCase()
                            .includes(appsSearchTerm.toLowerCase());
                        })
                    )
                    .map((app) => (
                      <Table.Tr key={app._id}>
                        <Table.Td style={styles.tableCell}>
                          <Group gap={6} wrap="nowrap" align="center">
                            <Text fw={500}>{app.appName}</Text>
                            {/* Users across all licenses of this app (sum of the per-license counts). */}
                            <Badge
                              color="gray"
                              variant="light"
                              size="sm"
                              leftSection={<IconUsers size={12} />}
                              style={{ flexShrink: 0 }}
                            >
                              {Array.from(app.licensesToSubscriptions?.values() ?? []).reduce(
                                (total, { subscriptions }) => total + subscriptions.length,
                                0
                              )}
                            </Badge>
                          </Group>
                          <Text size="xs" color="dimmed">
                            {app.summaryDescription || 'No description'}
                          </Text>
                        </Table.Td>
                        <Table.Td style={styles.tableCell}>
                          {app.licensesToSubscriptions && app.licensesToSubscriptions.size > 0 ? (
                            // Inline grid: counts form one column right after the longest name.
                            <div
                              style={{
                                display: 'inline-grid',
                                gridTemplateColumns: 'auto auto',
                                alignItems: 'center',
                                columnGap: '8px',
                                rowGap: '4px',
                              }}
                            >
                              {Array.from(app.licensesToSubscriptions.values()).map(
                                ({ license, subscriptions }) => (
                                  <React.Fragment key={license._id}>
                                    <Text size="xs" style={{ color: '#374151' }}>
                                      {license.licenseName}
                                    </Text>
                                    <Badge
                                      color="gray"
                                      variant="light"
                                      size="xs"
                                      leftSection={<IconUsers size={10} />}
                                      style={{ justifySelf: 'end' }}
                                    >
                                      {subscriptions.length}
                                    </Badge>
                                  </React.Fragment>
                                )
                              )}
                            </div>
                          ) : (
                            <Badge color="gray" variant="light" size="sm">
                              No licenses
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td style={styles.fitCell}>
                          {app.plattforms && app.plattforms.length > 0 ? (
                            // max-content: badges truncate their label, so without it the fit column squeezes them.
                            <Stack gap={4} align="flex-start" w="max-content">
                              {app.plattforms.map((platform) => (
                                <Badge key={platform} color="gray" variant="light" size="sm">
                                  {platform}
                                </Badge>
                              ))}
                            </Stack>
                          ) : (
                            <Badge color="gray" variant="light" size="sm">
                              No platforms
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td style={styles.fitCell}>
                          {editingInlineLimits?.appId === app._id &&
                          editingInlineLimits?.field === 'userLimit' ? (
                            <NumberInput
                              value={inlineLimitValue}
                              onChange={(value) =>
                                setInlineLimitValue(typeof value === 'number' ? value : 0)
                              }
                              onKeyDown={(e) => handleInlineKeyPress(e, app._id, 'userLimit')}
                              onBlur={() => handleInlineBlur(app._id, 'userLimit')}
                              min={0}
                              size="xs"
                              style={{ width: '80px' }}
                              autoFocus
                            />
                          ) : (
                            <Badge
                              color="blue"
                              variant="light"
                              style={{ cursor: 'pointer' }}
                              onClick={() =>
                                handleInlineEditStart(app._id, 'userLimit', app.userLimit || 0)
                              }
                            >
                              {app.userLimit || 0}
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td style={styles.fitCell}>
                          {editingInlineLimits?.appId === app._id &&
                          editingInlineLimits?.field === 'modelLimit' ? (
                            <NumberInput
                              value={inlineLimitValue}
                              onChange={(value) =>
                                setInlineLimitValue(typeof value === 'number' ? value : 0)
                              }
                              onKeyDown={(e) => handleInlineKeyPress(e, app._id, 'modelLimit')}
                              onBlur={() => handleInlineBlur(app._id, 'modelLimit')}
                              min={0}
                              size="xs"
                              style={{ width: '80px' }}
                              autoFocus
                            />
                          ) : (
                            <Badge
                              color="blue"
                              variant="light"
                              style={{ cursor: 'pointer' }}
                              onClick={() =>
                                handleInlineEditStart(app._id, 'modelLimit', app.modelLimit || 0)
                              }
                            >
                              {app.modelLimit || 0}
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td style={styles.fitCell}>
                          <Group gap={4} wrap="nowrap" preventGrowOverflow={false} w="max-content">
                            {(['LISTED', 'UNLISTED', 'PENDING'] as const).map((status, index) => (
                              <React.Fragment key={status}>
                                {index > 0 && (
                                  <Text size="xs" c="dimmed">
                                    /
                                  </Text>
                                )}
                                <Badge color="gray" variant="light" size="sm">
                                  {app.listings?.filter((listing) => listing.status === status)
                                    .length || 0}
                                </Badge>
                              </React.Fragment>
                            ))}
                          </Group>
                        </Table.Td>
                        <Table.Td style={styles.tableCell}>
                          {app.adminUser_ids && app.adminUser_ids.length > 0 ? (
                            <Stack gap={4}>
                              {app.adminUser_ids.map((adminId: string) => {
                                const adminLabel = fullUsers.get(adminId)?.email || `Unknown (${adminId})`;
                                return (
                                  // Badge truncates long emails; hover or tap shows the full one.
                                  <Tooltip
                                    key={adminId}
                                    label={adminLabel}
                                    withArrow
                                    events={{ hover: true, focus: true, touch: true }}
                                  >
                                    <Badge color="gray" variant="light" size="sm" tt="none">
                                      {adminLabel}
                                    </Badge>
                                  </Tooltip>
                                );
                              })}
                            </Stack>
                          ) : (
                            <Badge color="gray" variant="light" size="sm">
                              {t('masterAdmin.emptyStates.noAdminsFound')}
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td style={styles.fitCell}>
                          {/* Icon-only and stacked so the data columns get the width. */}
                          <Stack gap={6} align="flex-end">
                            <Tooltip label={t('masterAdmin.actions.edit')} withArrow>
                              <EditorChipButton
                                accentColor={accentColor}
                                leftIcon={<IconSettings size={14} stroke={1.75} />}
                                onClick={() => handleEditAppLimits(app)}
                                aria-label={t('masterAdmin.actions.edit')}
                              />
                            </Tooltip>
                            <Tooltip label={t('masterAdmin.actions.delete')} withArrow>
                              <EditorChipButton
                                variant="danger"
                                leftIcon={<IconTrash size={14} stroke={1.75} />}
                                onClick={() => handleDeleteApp(app._id)}
                                aria-label={t('masterAdmin.actions.delete')}
                              />
                            </Tooltip>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
                </Table>
              </div>
            )}
          </CollapsibleSection>

          {/* MasterAdmin Licenses Section */}
          <CollapsibleSection
            icon={<IconLicense size={18} stroke={1.75} />}
            title={t('masterAdmin.licenses.title')}
            subtitle={t('masterAdmin.licenses.manage')}
            accentColor="#10b981"
            sectionStyle={styles.section}
            headerStyle={styles.cardHeader}
          >

            <Group justify="space-between" mb="md">
              <EditorChipButton
                variant="primary"
                accentColor={accentColor}
                leftIcon={<IconPlus size={15} stroke={1.75} />}
                onClick={handleAddLicense}
              >
                {t('masterAdmin.licenses.create')}
              </EditorChipButton>
            </Group>

            {licenses.length === 0 ? (
              <div style={styles.emptyState}>
                <IconLicense size={48} color="#d1d5db" />
                <Text size="lg" mt="md" fw={500}>
                  {t('masterAdmin.emptyStates.noLicensesFound')}
                </Text>
                <Text size="sm" c="dimmed" mt="xs">
                  {t('masterAdmin.licenses.createFirst')}
                </Text>
              </div>
            ) : (
              <div style={styles.tableContainer}>
                <Table style={styles.table}>
                  <Table.Thead style={styles.tableHeader}>
                    <Table.Tr>
                      <Table.Th style={styles.tableHeaderCell}>
                        {t('masterAdmin.tables.licenseName')}
                      </Table.Th>
                      <Table.Th style={styles.tableHeaderCell}>{t('masterAdmin.tables.appName')}</Table.Th>
                      <Table.Th style={styles.tableHeaderCell}>
                        {t('masterAdmin.tables.featureFlags')}
                      </Table.Th>
                      <Table.Th style={styles.fitHeaderCell}>{t('masterAdmin.tables.actions')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                  {licenses.map((license) => (
                    <Table.Tr key={license._id}>
                      <Table.Td style={styles.tableCell}>
                        <Text fw={500}>{license.licenseName}</Text>
                      </Table.Td>
                      <Table.Td style={styles.tableCell}>
                        <Badge color="gray" variant="light">
                          {apps.find((a) => a._id === license.app_id)?.appName ||
                            t('masterAdmin.permissions.appNotFound')}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={styles.tableCell}>
                        {/* Two sub-columns filled top to bottom: first half (rounded up) left, rest right. */}
                        <div
                          style={{
                            display: 'grid',
                            gridAutoFlow: 'column',
                            gridTemplateRows: `repeat(${Math.ceil(
                              Object.keys(license.featureFlags || {}).length / 2
                            )}, auto)`,
                            columnGap: '24px',
                            rowGap: '2px',
                            justifyContent: 'start',
                          }}
                        >
                          {Object.entries(license.featureFlags || {}).map(([key, value]) => (
                            <Group key={key} gap={4} align="center" wrap="nowrap">
                              <Text size="xs" style={{ minWidth: 140, flex: '0 0 140px' }}>
                                {key.charAt(0).toUpperCase() +
                                  key.slice(1).replace(/([A-Z])/g, ' $1')}
                              </Text>
                              <Text size="xs" style={{ flex: '0 0 auto' }}>
                                {
                                  {
                                    [FeatureFlag.ENABLED]: '✅',
                                    [FeatureFlag.DISABLED]: '❌',
                                    [FeatureFlag.UNDEFINED]: '➖',
                                  }[value]
                                }
                              </Text>
                            </Group>
                          ))}
                        </div>
                      </Table.Td>
                      <Table.Td style={styles.fitCell}>
                        <Stack gap={6} align="flex-end">
                          <Tooltip label={t('masterAdmin.actions.edit')} withArrow>
                            <EditorChipButton
                              accentColor={accentColor}
                              leftIcon={<IconEdit size={14} stroke={1.75} />}
                              onClick={() => handleEditLicense(license)}
                              aria-label={t('masterAdmin.actions.edit')}
                            />
                          </Tooltip>
                          <Tooltip label={t('masterAdmin.actions.delete')} withArrow>
                            <EditorChipButton
                              variant="danger"
                              leftIcon={<IconTrash size={14} stroke={1.75} />}
                              onClick={() => handleDeleteLicense(license._id)}
                              aria-label={t('masterAdmin.actions.delete')}
                            />
                          </Tooltip>
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
                </Table>
              </div>
            )}
          </CollapsibleSection>

          {/* MasterAdmin Admins Section */}
          <CollapsibleSection
            icon={<IconUsers size={18} stroke={1.75} />}
            title={t('masterAdmin.admins.title')}
            subtitle={t('masterAdmin.admins.manage')}
            accentColor="#f59e0b"
            sectionStyle={styles.section}
            headerStyle={styles.cardHeader}
          >

            <Group justify="space-between" mb="md">
              <EditorChipButton
                variant="primary"
                accentColor={accentColor}
                leftIcon={<IconPlus size={15} stroke={1.75} />}
                onClick={handleAddAdmin}
              >
                {t('masterAdmin.admins.add')}
              </EditorChipButton>
            </Group>

            {admins.length === 0 ? (
              <div style={styles.emptyState}>
                <IconUsers size={48} color="#d1d5db" />
                <Text size="lg" mt="md" fw={500}>
                  {t('masterAdmin.emptyStates.noAdminsFound')}
                </Text>
                <Text size="sm" c="dimmed" mt="xs">
                  {t('masterAdmin.admins.addFirst')}
                </Text>
              </div>
            ) : (
              <div style={styles.tableContainer}>
                <Table style={styles.table}>
                  <Table.Thead style={styles.tableHeader}>
                    <Table.Tr>
                      <Table.Th style={styles.tableHeaderCell}>{t('masterAdmin.tables.email')}</Table.Th>
                      <Table.Th style={styles.tableHeaderCell}>{t('masterAdmin.tables.username')}</Table.Th>
                      <Table.Th style={styles.tableHeaderCell}>{t('masterAdmin.tables.appName')}</Table.Th>
                      <Table.Th style={styles.fitHeaderCell}>{t('masterAdmin.tables.actions')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                  {admins.map((admin) => (
                    <Table.Tr key={admin._id}>
                      <Table.Td style={styles.tableCell}>
                        <Text fw={500}>{admin.email}</Text>
                      </Table.Td>
                      <Table.Td style={styles.tableCell}>
                        <Badge color="gray" variant="light">
                          {admin.username || 'N/A'}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={styles.tableCell}>
                        {Array.isArray(admin.apps) && admin.apps.length > 0 ? (
                          <Stack gap={4}>
                            {[...admin.apps]
                              .sort((a: any, b: any) =>
                                String(a.appName ?? '').localeCompare(String(b.appName ?? ''), undefined, {
                                  sensitivity: 'base',
                                })
                              )
                              .map((app: any) => (
                                <Badge key={app._id} color="gray" variant="light" size="sm">
                                  {app.appName}
                                </Badge>
                              ))}
                          </Stack>
                        ) : (
                          <Badge color="gray" variant="light" size="sm">
                            {t('masterAdmin.emptyStates.noAppsFound')}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td style={styles.fitCell}>
                        <Stack gap={6} align="flex-end">
                          <Tooltip label={t('masterAdmin.actions.edit')} withArrow>
                            <EditorChipButton
                              accentColor={accentColor}
                              leftIcon={<IconEdit size={14} stroke={1.75} />}
                              onClick={() => handleEditAdmin(admin)}
                              aria-label={t('masterAdmin.actions.edit')}
                            />
                          </Tooltip>
                          <Tooltip label={t('masterAdmin.actions.delete')} withArrow>
                            <EditorChipButton
                              variant="danger"
                              leftIcon={<IconTrash size={14} stroke={1.75} />}
                              onClick={() => handleDeleteAdmin(admin.email)}
                              aria-label={t('masterAdmin.actions.delete')}
                            />
                          </Tooltip>
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
                </Table>
              </div>
            )}
          </CollapsibleSection>

          {/* MasterAdmin Users Management Section */}
          <CollapsibleSection
            title={t('masterAdmin.users.title')}
            subtitle={t('masterAdmin.users.description')}
            accentColor="#6366f1"
            sectionStyle={styles.section}
            headerStyle={styles.cardHeader}
          >

            <TextInput
              placeholder={t('masterAdmin.users.searchPlaceholder')}
              value={usersSearchTerm}
              onChange={(e) => setUsersSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              size="sm"
              style={{ width: '100%', marginBottom: '16px' }}
            />

            <UserTable
              fullUsers={fullUsers}
              subscriptionInfo={subscriptionInfo}
              searchTerm={usersSearchTerm}
              onUpdateUser={(userId, updatedUser) => {
                const updatedUsers = new Map(fullUsers);
                updatedUsers.set(userId, updatedUser);
                setFullUsers(updatedUsers);
              }}
              styles={styles}
              theme={theme}
              isDark={isDark}
            />
          </CollapsibleSection>

          {/* MasterAdmin Modals */}
          {/* License Modal */}
          <LicenseModal
            opened={openLicenseModal}
            onClose={() => setOpenLicenseModal(false)}
            editingLicense={editingLicense}
            licenseFormData={licenseFormData}
            onLicenseFormDataChange={setLicenseFormData}
            onSave={handleSaveLicense}
            apps={apps}
            accentColor={accentColor}
          />

          {/* Add/Edit Admin Modal */}
          <Modal
            opened={openAdminModal}
            onClose={() => {
              setOpenAdminModal(false);
              setEditingAdmin(null);
            }}
            title={
              <Group>
                <IconUsers size={20} color="#f59e0b" />
                <Text fw={600}>
                  {editingAdmin ? t('masterAdmin.admins.update') : t('masterAdmin.admins.add')}
                </Text>
              </Group>
            }
            size="md"
          >
            <Stack gap="md">
              <TextInput
                label={t('masterAdmin.admins.email')}
                placeholder={t('masterAdmin.admins.emailPlaceholder')}
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
                leftSection={<IconUsers size={16} />}
              />
              <MultiSelect
                label={t('masterAdmin.admins.selectApps')}
                placeholder={t('masterAdmin.admins.selectApps')}
                value={selectedAppIds}
                onChange={setSelectedAppIds}
                data={apps.map((app) => ({ value: String(app._id), label: app.appName }))}
                required
                leftSection={<IconShield size={16} />}
              />
              <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
                <Text size="sm">{t('masterAdmin.admins.emailInfo')}</Text>
              </Alert>
              <Group justify="flex-end" mt="md" gap={8}>
                <EditorChipButton accentColor={accentColor} onClick={() => setOpenAdminModal(false)}>
                  {t('masterAdmin.forms.cancel')}
                </EditorChipButton>
                <EditorChipButton
                  variant="primary"
                  accentColor={accentColor}
                  leftIcon={<IconCheck size={15} stroke={1.75} />}
                  onClick={handleSaveAdmin}
                  disabled={!adminEmail || selectedAppIds.length === 0}
                >
                  {editingAdmin ? t('masterAdmin.admins.update') : t('masterAdmin.admins.add')}
                </EditorChipButton>
              </Group>
            </Stack>
          </Modal>

          {/* Edit App Settings Modal */}
          <Modal
            opened={!!editingAppLimits}
            onClose={() => setEditingAppLimits(null)}
            title={
              <Group>
                <IconSettings size={20} color="#3b82f6" />
                <Text fw={600}>
                  {t('masterAdmin.apps.settings')} - {editingAppLimits?.appName}
                </Text>
              </Group>
            }
            size="lg"
          >
            <Stack gap="md">
              <TextInput
                label={t('masterAdmin.apps.name')}
                placeholder={t('masterAdmin.apps.namePlaceholder')}
                value={limitsFormData.appName}
                onChange={(e) => setLimitsFormData({ ...limitsFormData, appName: e.target.value })}
                required
                leftSection={<IconShield size={16} />}
              />
              <TextInput
                label={t('masterAdmin.apps.description')}
                placeholder={t('masterAdmin.apps.descriptionPlaceholder')}
                value={limitsFormData.summaryDescription}
                onChange={(e) =>
                  setLimitsFormData({ ...limitsFormData, summaryDescription: e.target.value })
                }
                leftSection={<IconSettings size={16} />}
              />
              <MultiSelect
                label={t('masterAdmin.apps.platforms')}
                placeholder={t('masterAdmin.apps.platformsPlaceholder')}
                value={limitsFormData.plattforms}
                onChange={(value) => setLimitsFormData({ ...limitsFormData, plattforms: value })}
                data={[
                  { value: 'web', label: t('masterAdmin.platforms.web') },
                  { value: 'mobile', label: t('masterAdmin.platforms.mobile') },
                  { value: 'desktop', label: t('masterAdmin.platforms.desktop') },
                  { value: 'tablet', label: t('masterAdmin.platforms.tablet') },
                  { value: 'vr', label: t('masterAdmin.platforms.vr') },
                  { value: 'ar', label: t('masterAdmin.platforms.ar') },
                ]}
                leftSection={<IconSettings size={16} />}
              />
              <NumberInput
                label={t('masterAdmin.tables.userLimit')}
                placeholder={t('masterAdmin.tables.userLimit')}
                value={limitsFormData.userLimit}
                onChange={(value) =>
                  setLimitsFormData({ ...limitsFormData, userLimit: Number(value) || 0 })
                }
                min={0}
                required
                leftSection={<IconUsers size={16} />}
              />
              <NumberInput
                label={t('masterAdmin.tables.modelLimit')}
                placeholder={t('masterAdmin.tables.modelLimit')}
                value={limitsFormData.modelLimit}
                onChange={(value) =>
                  setLimitsFormData({ ...limitsFormData, modelLimit: Number(value) || 0 })
                }
                min={0}
                required
                leftSection={<IconShield size={16} />}
              />
              <Group justify="flex-end" mt="md" gap={8}>
                <EditorChipButton accentColor={accentColor} onClick={() => setEditingAppLimits(null)}>
                  {t('masterAdmin.forms.cancel')}
                </EditorChipButton>
                <EditorChipButton
                  variant="primary"
                  accentColor={accentColor}
                  leftIcon={<IconCheck size={15} stroke={1.75} />}
                  onClick={handleSaveAppLimits}
                >
                  {t('masterAdmin.forms.updateSettings')}
                </EditorChipButton>
              </Group>
            </Stack>
          </Modal>

          {/* Error Modal */}
          <Modal
            opened={errorModal.opened}
            onClose={() => setErrorModal({ opened: false, title: '', message: '' })}
            title={
              <Group>
                <IconAlertCircle size={20} color="#dc2626" />
                <Text fw={600} c="red">
                  {errorModal.title}
                </Text>
              </Group>
            }
            size="md"
          >
            <Stack gap="md">
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                <Text size="sm">{errorModal.message}</Text>
              </Alert>
              <Group justify="flex-end" mt="md" gap={8}>
                <EditorChipButton
                  variant="danger"
                  leftIcon={<IconX size={15} stroke={1.75} />}
                  onClick={() => setErrorModal({ opened: false, title: '', message: '' })}
                >
                  {t('masterAdmin.actions.close')}
                </EditorChipButton>
              </Group>
            </Stack>
          </Modal>
        </Container>
      </Center>
    </SessionAuth>
  );
}
